/*
 * A reactive list powered by one of several backends.
 */
import { Component, Prop, Provide, Vue } from "vue-facing-decorator";
import { type BufferedItems } from "../../Stores/VueList/BufferedItems";
import { type IConversions } from "../../Stores/VueList/IConversions";
import Config from "./Items/Config";
import { type IVueListOptions, type IBackendOptions } from "./VueListOptions";
import { type Component as VueComponent } from "vue";

@Component
export class VueListBase<S extends string | number, T, O extends IBackendOptions> extends Vue {
	@Prop({ type: Object, required: true }) protected bufferedItems!: BufferedItems<S>;
	@Prop({ type: Object, required: true }) conversions!: IConversions<S, T>;
	@Prop({ required: true }) protected template!: string | IVueListOptions;
	@Prop({ required: false, type: Object, default: {} }) itemProps!: Record<string, any>;

	/**
	 * Make sure that the item at offset is visible, possibly focusing it.
	 * @param offset zero-based index into items.
	 */
	public goto(_offset: number): void {
		void this.preload(0);
	}

	// Discard the content of this list from the store (and reload it from the
	// server.) Note that this does not reset the conversions in the
	// conversion store, use invalidate() on an item for this.
	@Provide() protected reset(until?: Promise<unknown>): Promise<void> { return this.bufferedItems.invalidate(until); }

	// Fetch more items (the previous and the next page or more) in the background.
	protected preload(currentOffset: number): Promise<unknown> {
		return this.bufferedItems.preload(currentOffset);
	}

	get options(): IVueListOptions<O> {
		if (typeof this.template === "string") {
			return Config[this.template] as IVueListOptions<O>;
		}
		return this.template as IVueListOptions<O>;
	}

	get backendOptions(): O {
		return this.options.backendOptions;
	}

	get component(): VueComponent {
		return this.options.component;
	}

	/**
	 * Generates unique keys for Vue list rendering, handling duplicate items.
	 *
	 * @remarks
	 * Transitions still don't work properly for duplicate items, because we still
	 * can't distinguish between them. As a result of that, the last occurrence is
	 * the one that is removed always.
	 *
	 * @returns Array of unique string keys in the format "{item}_{occurrence_count}"
	 */
	get keys(): string[] {
		const occurrenceCount = new Map<S, number>();
		const keys: string[] = [];

		for (const item of this.items) {
			const count = (occurrenceCount.get(item) ?? 0) + 1;
			occurrenceCount.set(item, count);

			keys.push(`${String(item)}_${count - 1}`);
		}

		return keys;
	}

	// The items displayed in this list.
	get items(): S[] { return this.bufferedItems.value || []; }

	// The number of items to preload for the next page.
	// Note that this should be the maximum number of items that fit onto one page in any
	// resolution so that a higher resolution page does not create empty columns.
	protected get pageSize(): number {
		// The default options, i.e., the ones not inside .responsive are for the maximum resolution.
		return this.options.pageSize;
	}

	// An approximate count of the total numbers of items that can be provided by the backend.
	// Might change as more items are loaded.
	protected get totalCount(): number { return this.bufferedItems.totalCountEstimate; }

	get isEmpty(): boolean { return this.bufferedItems.totalCountEstimate === 0; }

	get messageIfEmpty(): string {
		if (typeof (this.template) == "string") {
			return this.$tr(`${this.template}_EmptyList`);
		}
		return this.$tr("EmptyList");
	}

	protected get isResetting(): number {
		return this.bufferedItems.isResetting;
	}
}
